import NavigationUtils from '../../utils/navigation.utils';
import AppBar from '../../components/shared/AppBar/AppBar';
import { useEffect, useRef, useState } from 'preact/hooks';
import Title from '../../components/shared/Typography/Title';
import BodyText from '../../components/shared/Typography/BodyText';
import style from './P2PManualPhoneNumberPage.css';
import useTranslation from '../../localization/i18n.context';
import P2PPhoneNumberInput from '../../components/PeerToPeer/P2PPhoneNumberInput/P2PPhoneNumberInput';
import Button from '../../components/shared/Button/Button';
import ContactsService from '../../services/contacts.service';
import { renderIf } from '../../utils/render.utils';
import LoadingScreen from '../../components/shared/LoadingScreen/LoadingScreen';
import Alert from '../../components/shared/Alert/Alert';

const MIN_PHONE_NUMBER_LENGTH = 11;

export default function P2PManualPhoneNumberPage({ cardUidFrom }) {
  const backRef = useRef(null);
  const { t, getOrganization } = useTranslation();

  //ATTENTION: In case we migrate to another country, the country code cant be harcoded. We could pick this from BE
  const [number, setNumber] = useState({ code: '2', value: '' });
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [continueEnable, setContinueEnable] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [rateLimitReached, setRateLimitReached] = useState(false);

  function onChangeNumberInput(value) {
    setNumber(prevState => ({ ...prevState, value }));
    setHasError(false);
    setErrorMessage(null);
  }

  function onContinueClick() {
    setIsLoading(true);

    const phoneNumber = `${number.code}${number.value.replace(/ /g, '')}`;
    let contactId;

    ContactsService.getCaaSContact(phoneNumber)
      .then(response => {
        if (response.data == null || response.data.activeCards === 0) {
          return Promise.reject({
            statusCode: 404,
          });
        }

        return NavigationUtils.navigate(
          NavigationUtils.Routes.P2P_CONTACT_CARD_LIST.replace(
            ':contactUserId',
            response.data.id,
          ).replace(':cardUid', cardUidFrom),
          false,
        );
      })
      .catch(error => {
        if (error.statusCode === 404) {
          setHasError(true);
          setErrorMessage(
            t('This number does not belong to a {company_name} card', {
              '{company_name}':
                getOrganization(NavigationUtils.getOrganizationCode()) ?? '',
            }),
          );
          setIsLoading(false);
          return;
        }

        if (error.statusCode === 429) {
          setRateLimitReached(true);
          setIsLoading(false);
          return;
        }

        setHasError(true);
        setErrorMessage(t('Something went wrong'));
        setIsLoading(false);
      });
  }

  useEffect(() => {
    setContinueEnable(
      number.value.replace(/ /g, '').length >= MIN_PHONE_NUMBER_LENGTH,
    );
  }, [number]);

  return (
    <>
      {renderIf(isLoading, <LoadingScreen />)}
      {renderIf(
        !isLoading,
        <>
          <AppBar
            onBackClick={() => {
              NavigationUtils.back();
            }}
            onCloseClick={() => {
              NavigationUtils.endFlow();
            }}
            backRef={backRef}
            className="fixed-bar"
          />
          <div className={style.container}>
            <div className={style.head}>
              <Title
                className={style.title}
                text={t('Enter your recipient’s phone number')}
              />
              <BodyText
                text={t(
                  'The phone number must be linked to a {company_name} card',
                  {
                    '{company_name}':
                      getOrganization(NavigationUtils.getOrganizationCode()) ??
                      '',
                  },
                )}
                className={style.subtitle}
              />
            </div>
            {rateLimitReached && (
              <Alert
                className={style.alert}
                message={t('Too many attempts. Please try again later.')}
                type={Alert.Type.WARNING}
                hasIcon
              />
            )}
            <P2PPhoneNumberInput
              className={style.input}
              value={number.value}
              countryCode={number.code}
              disabled={rateLimitReached}
              onChange={value => onChangeNumberInput(value)}
              onClear={() => onChangeNumberInput('')}
              hasError={hasError}
              errorMessage={errorMessage}
            />
            <div className="fixed-cta">
              <Button
                fullWidth
                disabled={!continueEnable || rateLimitReached}
                onClick={onContinueClick}
              >
                {t('Continue')}
              </Button>
            </div>
          </div>
        </>,
      )}
    </>
  );
}
